import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Plane, TowerControl, Route as RouteIcon, MapPin, Calendar, Users, Ticket, Globe2, PlaneTakeoff, PlaneLanding } from "lucide-react";
import api from "../lib/api";
import { googleFlightsUrl } from "../lib/googleFlights";

const TABS = [
  { key: "flights", label: "Flights", icon: PlaneTakeoff },
  { key: "airlines", label: "Airlines", icon: Plane },
  { key: "airports", label: "Airports", icon: TowerControl },
  { key: "routes", label: "Routes", icon: RouteIcon },
];

const FEATURES = [
  { icon: Plane, title: "Comprehensive Data", sub: "In-depth information on thousands of airlines and airports worldwide.", to: "/airports", testid: "aa-feature-data" },
  { icon: MapPin, title: "Live Flight Status", sub: "Real-time updates and tracking for flights around the globe.", to: "/my-flight", testid: "aa-feature-status" },
  { icon: Globe2, title: "Route Explorer", sub: "Discover routes, compare options and plan your next journey.", to: "/map", testid: "aa-feature-routes" },
  { icon: Ticket, title: "Travel Guides & Tips", sub: "Expert travel advice, airport guides and helpful tips for travelers.", to: "/guides", testid: "aa-feature-guides" },
];

const DESTINATIONS = [
  { city: "New York", code: "JFK", img: "/img/dest-newyork.jpg", sub: "John F. Kennedy Intl" },
  { city: "Paris", code: "CDG", img: "/img/dest-paris.jpg", sub: "Charles de Gaulle" },
  { city: "London", code: "LHR", img: "/img/dest-london.jpg", sub: "Heathrow" },
  { city: "Dubai", code: "DXB", img: "/img/dest-dubai.jpg", sub: "Dubai Intl" },
];

function SearchCard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("flights");
  const [f, setF] = useState({ from: "", to: "", depart: "", pax: 1, q: "" });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    if (tab === "flights") {
      if (!f.from.trim() || !f.to.trim()) return;
      window.open(googleFlightsUrl({ type: "oneway", from: f.from.trim(), to: f.to.trim(), depart: f.depart, passengers: Number(f.pax) || 1 }), "_blank", "noopener");
    } else if (tab === "airlines") {
      if (f.q.trim()) nav(`/airlines/directory?q=${encodeURIComponent(f.q.trim())}`);
    } else if (tab === "airports") {
      if (f.q.trim()) nav(`/airports/directory?q=${encodeURIComponent(f.q.trim())}`);
    } else {
      const a = f.from.trim().toUpperCase(), b = f.to.trim().toUpperCase();
      if (/^[A-Z]{3}$/.test(a) && /^[A-Z]{3}$/.test(b)) nav(`/route/${a}-${b}`);
    }
  };

  const field = "flex items-center gap-2 border border-zinc-200 rounded-lg px-3 py-2.5 bg-white focus-within:border-blue-600 transition-colors min-w-0";
  const input = "w-full min-w-0 text-sm outline-none placeholder:text-zinc-400";

  return (
    <div className="bg-white rounded-2xl shadow-xl shadow-blue-900/10 border border-zinc-100 overflow-hidden" data-testid="aa-search-card">
      <div className="flex border-b border-zinc-100 overflow-x-auto">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`aa-tab-${key}`}
            className={`shrink-0 inline-flex items-center gap-1.5 px-4 sm:px-6 py-3.5 text-sm font-700 border-b-2 -mb-px transition-colors ${tab === key ? "border-blue-700 text-blue-800" : "border-transparent text-zinc-500 hover:text-zinc-800"}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>
      <form onSubmit={submit} className="p-4 sm:p-5 flex flex-col lg:flex-row gap-2.5" data-testid="aa-search-card-form">
        {tab === "flights" && (
          <>
            <label className={`${field} flex-1`}><PlaneTakeoff className="w-4 h-4 text-zinc-400 shrink-0" />
              <span className="min-w-0 flex-1"><span className="block text-[10px] font-700 text-zinc-400 uppercase">From</span>
                <input value={f.from} onChange={set("from")} placeholder="City or Airport" className={input} data-testid="aa-flight-from" /></span></label>
            <label className={`${field} flex-1`}><PlaneLanding className="w-4 h-4 text-zinc-400 shrink-0" />
              <span className="min-w-0 flex-1"><span className="block text-[10px] font-700 text-zinc-400 uppercase">To</span>
                <input value={f.to} onChange={set("to")} placeholder="City or Airport" className={input} data-testid="aa-flight-to" /></span></label>
            <label className={`${field} lg:w-44`}><Calendar className="w-4 h-4 text-zinc-400 shrink-0" />
              <span className="min-w-0 flex-1"><span className="block text-[10px] font-700 text-zinc-400 uppercase">Depart</span>
                <input type="date" value={f.depart} onChange={set("depart")} className={input} data-testid="aa-flight-date" /></span></label>
            <label className={`${field} lg:w-40`}><Users className="w-4 h-4 text-zinc-400 shrink-0" />
              <span className="min-w-0 flex-1"><span className="block text-[10px] font-700 text-zinc-400 uppercase">Passengers</span>
                <select value={f.pax} onChange={set("pax")} className={`${input} bg-white`} data-testid="aa-flight-pax">
                  {[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n} Passenger{n > 1 ? "s" : ""}</option>)}
                </select></span></label>
          </>
        )}
        {(tab === "airlines" || tab === "airports") && (
          <label className={`${field} flex-1`}><Search className="w-4 h-4 text-zinc-400 shrink-0" />
            <span className="min-w-0 flex-1"><span className="block text-[10px] font-700 text-zinc-400 uppercase">{tab === "airlines" ? "Airline" : "Airport"}</span>
              <input value={f.q} onChange={set("q")} placeholder={tab === "airlines" ? "Search 6,000+ airlines by name or code…" : "Search 85,000+ airports by name, city or IATA…"} className={input} data-testid={`aa-${tab}-q`} /></span></label>
        )}
        {tab === "routes" && (
          <>
            <label className={`${field} flex-1`}><PlaneTakeoff className="w-4 h-4 text-zinc-400 shrink-0" />
              <span className="min-w-0 flex-1"><span className="block text-[10px] font-700 text-zinc-400 uppercase">From (IATA)</span>
                <input value={f.from} onChange={set("from")} placeholder="e.g. DEL" maxLength={3} className={`${input} uppercase notranslate`} data-testid="aa-route-from" /></span></label>
            <label className={`${field} flex-1`}><PlaneLanding className="w-4 h-4 text-zinc-400 shrink-0" />
              <span className="min-w-0 flex-1"><span className="block text-[10px] font-700 text-zinc-400 uppercase">To (IATA)</span>
                <input value={f.to} onChange={set("to")} placeholder="e.g. BOM" maxLength={3} className={`${input} uppercase notranslate`} data-testid="aa-route-to" /></span></label>
          </>
        )}
        <button type="submit" data-testid="aa-search-card-submit"
          className="shrink-0 inline-flex items-center justify-center gap-1.5 bg-blue-700 hover:bg-blue-800 text-white font-800 text-sm rounded-lg px-6 py-3 transition-colors">
          <Search className="w-4 h-4" />
          {tab === "flights" ? "Search Flights" : tab === "routes" ? "Find Route" : `Search ${tab === "airlines" ? "Airlines" : "Airports"}`}
        </button>
      </form>
    </div>
  );
}

export default function AAHome() {
  const [stats, setStats] = useState(null);
  const [news, setNews] = useState([]);

  useEffect(() => {
    api.get("/v1/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/aviation-news?limit=8").then((r) => setNews(r.data.items || [])).catch(() => {});
  }, []);

  const fmt = (n) => (n == null ? "—" : n >= 1000 ? `${Math.floor(n / 1000)}K+` : n.toLocaleString());

  return (
    <div data-testid="aa-home">
      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0">
          <img src="/img/aa-hero.jpg" alt="Airport terminal at sunset" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/60 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-16 sm:pt-24 pb-28 sm:pb-32">
          <h1 className="font-heading font-900 text-4xl sm:text-5xl lg:text-6xl text-[#0A2A5E] leading-tight max-w-xl" data-testid="aa-hero-title">
            Your Journey<br />Starts Here
          </h1>
          <p className="text-zinc-700 text-base md:text-lg mt-4 max-w-md">
            The world's most comprehensive resource for airlines, airports, routes and travel information.
          </p>
        </div>
      </section>

      {/* Search card overlapping hero */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 -mt-20 sm:-mt-24 relative z-10">
        <SearchCard />
      </div>

      {/* Feature strip */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-5 lg:divide-x lg:divide-zinc-200">
          {FEATURES.map(({ icon: Icon, title, sub, to, testid }) => (
            <Link key={title} to={to} data-testid={testid} className="group flex gap-3 lg:px-5 first:lg:pl-0">
              <span className="inline-flex w-11 h-11 shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-700"><Icon className="w-5 h-5" /></span>
              <span className="min-w-0">
                <span className="block font-heading font-800 text-sm text-zinc-900 group-hover:text-blue-700 transition-colors">{title}</span>
                <span className="block text-xs text-zinc-500 mt-0.5">{sub}</span>
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* Popular destinations */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-8" data-testid="aa-destinations">
        <div className="text-center">
          <p className="text-[11px] font-800 uppercase tracking-[0.2em] text-blue-700">Explore the world</p>
          <h2 className="font-heading font-900 text-lg md:text-xl text-zinc-900 mt-1">Popular Destinations</h2>
          <p className="text-sm text-zinc-500 mt-1">Discover top airlines and airports around the world</p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          {DESTINATIONS.map((d) => (
            <Link key={d.code} to={`/airport/${d.code}`} data-testid={`aa-dest-${d.code}`}
              className="group relative rounded-xl overflow-hidden aspect-[4/3] block">
              <img src={d.img} alt={d.city} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
              <div className="absolute bottom-0 left-0 p-3 sm:p-4 text-white">
                <div className="font-heading font-800 text-base sm:text-lg">{d.city}</div>
                <div className="text-[11px] text-white/80">{d.sub} · <span className="font-mono font-700 notranslate">{d.code}</span></div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Stats strip */}
      <section className="bg-[#0A2A5E] text-white mt-6">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-8 grid grid-cols-3 sm:grid-cols-6 gap-4 text-center" data-testid="aa-stats">
          {[["Airports", stats?.airports], ["Airlines", stats?.airlines], ["Routes", stats?.routes],
            ["Aircraft", stats?.aircraft], ["Countries", stats?.countries], ["News links", stats?.news_links]].map(([l, v]) => (
            <div key={l}>
              <div className="font-heading font-900 text-xl sm:text-2xl text-amber-400 notranslate">{fmt(v)}</div>
              <div className="text-[10px] uppercase tracking-wide text-blue-200 font-700">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Latest aviation news */}
      {news.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 lg:px-8 py-12" data-testid="aa-home-news">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-heading font-900 text-lg md:text-xl text-zinc-900">Latest Aviation News</h2>
            <Link to="/aviation-news" className="text-xs font-800 text-blue-700 hover:underline" data-testid="aa-home-news-all">All news →</Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {news.slice(0, 8).map((n) => (
              <a key={n.url} href={n.url} target="_blank" rel="noopener noreferrer" data-testid="aa-home-news-card"
                className="border border-zinc-200 bg-white rounded-lg p-4 hover:border-blue-600 transition-colors min-w-0">
                <div className="flex items-center gap-2 text-[10px] font-800 uppercase tracking-wide">
                  <span className="text-amber-700">{n.category || "aviation"}</span>
                  {n.iata && <span className="text-blue-700 notranslate">{n.iata}</span>}
                </div>
                <div className="text-sm font-700 text-zinc-900 mt-1.5 line-clamp-3">{n.title}</div>
                <div className="text-[11px] text-zinc-400 mt-2 truncate">{n.source}</div>
              </a>
            ))}
          </div>
        </section>
      )}

      {/* Aviation API teaser */}
      <section className="bg-zinc-50 border-y border-zinc-200">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-12 grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <p className="text-[11px] font-800 uppercase tracking-[0.2em] text-amber-700">Aviation API v1</p>
            <h2 className="font-heading font-900 text-lg md:text-xl text-zinc-900 mt-2">One canonical record. Many sites.</h2>
            <p className="text-sm text-zinc-600 mt-2 max-w-lg">
              Every airport, airline and route carries an immutable canonical ID with source
              provenance. Network sites consume the read API — entities are never copied.
            </p>
            <Link to="/search" className="inline-block mt-4 text-sm font-800 text-blue-700 hover:underline" data-testid="aa-api-try-search">Try the search →</Link>
          </div>
          <pre className="bg-[#0A2A5E] text-sky-200 text-xs sm:text-sm rounded-xl p-5 overflow-x-auto notranslate" data-testid="aa-api-sample">{`GET /api/v1/airports/DEL
GET /api/v1/airlines/EK/routes
GET /api/v1/routes/DEL-BOM
GET /api/v1/news/latest?country=IN
GET /api/v1/search?q=heathrow`}</pre>
        </div>
      </section>
    </div>
  );
}
